import React, {
  createContext,
  useContext,
  useState,
  type PropsWithChildren,
} from 'react';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import type {
  MenuConfiguration,
  MenuItem,
} from '@redturtle/qubicaamf-theme/types/dropdown-menu';
import { DropdownMenu } from '@redturtle/qubicaamf-theme/components/Header/MegaMenu/DropdownMenu';
import styles from './styles.module.css';
import { useNavigationMenuState } from '@redturtle/qubicaamf-theme/helpers/hooks/useNavigationMenu.hook';
import { mergeData } from '@redturtle/qubicaamf-theme/helpers/dropdown-menu';
import cx from 'classnames';

export interface OrderedSubMenuItem extends MenuItem {
  order?: number;
  items: OrderedSubMenuItem[];
}
export type NavigationRootState = {
  navigation: {
    items: Array<MenuItem>;
  };
  dropdownMenuNavItems: {
    result: MenuConfiguration<OrderedSubMenuItem>[];
  };
};

type ActiveMenuItemState = {
  activeMenuItem: string | null;
  setActiveMenuItem: React.Dispatch<
    React.SetStateAction<ActiveMenuItemState['activeMenuItem']>
  >;
};

const ActiveMenuItemContext = createContext<ActiveMenuItemState | null>({
  activeMenuItem: null,
  setActiveMenuItem: () => {},
});
const ActiveMenuItemProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [state, setState] =
    useState<ActiveMenuItemState['activeMenuItem']>(null);

  return (
    <ActiveMenuItemContext.Provider
      value={{ activeMenuItem: state, setActiveMenuItem: setState }}
    >
      {children}
    </ActiveMenuItemContext.Provider>
  );
};
const useActiveMenuContext = () => useContext(ActiveMenuItemContext);

const LinkMenu = ({
  menuItem,
  index,
}: {
  menuItem: OrderedSubMenuItem;
  index: number;
}) => {
  const { setActiveMenuItem } = useActiveMenuContext();
  if (!menuItem?.linkUrl?.[0]?.['@id']) return null;
  return (
    <UniversalLink
      href={flattenToAppURL(menuItem.linkUrl?.[0]?.['@id'])}
      key={index}
      openLinkInNewTab={false}
      onMouseEnter={() => setActiveMenuItem(menuItem.linkUrl?.[0]?.['@id'])}
      className={cx(styles['dropdown-menu-navitem'], styles['link-item'])}
    >
      {menuItem.title}
    </UniversalLink>
  );
};

const MegaMenu: React.FC = () => {
  const { dropdownMenuNavItems, navItems } = useNavigationMenuState();
  const { items } = dropdownMenuNavItems?.[0];

  return (
    <ActiveMenuItemProvider>
      <nav className={styles['mainnav']}>
        {items?.map((menuItem, index) => {
          if (menuItem.mode === 'dropdown') {
            const navRoot =
              Array.isArray(menuItem.navigationRoot) &&
              menuItem.navigationRoot.length > 0
                ? menuItem.navigationRoot[0]
                : {};

            const navigationResultsForNavroot =
              navItems.find((ni) => ni['@id'] === navRoot['@id'])?.items ?? [];
            const navItem = {
              ...menuItem,
              items: mergeData(navigationResultsForNavroot, menuItem.items),
            };
            return <DropdownMenu key={index} item={navItem} />;
          } else
            return <LinkMenu menuItem={menuItem} index={index} key={index} />;
        })}
      </nav>
    </ActiveMenuItemProvider>
  );
};

export { MegaMenu, useActiveMenuContext };
